import { FlowerProduct, MemorialTree, ShoppingCart } from '../../../types'
import FlowerProductModel from '../FlowerProduct'
import MemorialTreeModel from '../MemorialTreeModel'

const cartEventData = (
  cart: ShoppingCart,
): { currency: 'USD'; value: number; items: Gtag.Item[] } => {
  const treeTotal = cart.memorial_trees_total_in_cents || 0
  const flowerTotal = cart.flower_order_total_in_cents || 0

  return {
    currency: 'USD',
    value: (treeTotal + flowerTotal) / 100.0,
    items: cart.memorial_tree_shopping_cart_items
      .map(MemorialTreeModel.toGaItem)
      .concat(cart.flower_shopping_cart_items.map(FlowerProductModel.cartProductToGaItem)),
  }
}

const EventTrackingModel = {
  trackCheckoutInitiated: (cart: ShoppingCart): void => {
    if (!gtag) return

    gtag('event', 'begin_checkout', cartEventData(cart))
  },
  trackContactEmail: (): void => {
    if (!gtag) return

    gtag('event', 'contact', {
      event_category: 'email',
    })
  },
  trackContactPhone: (): void => {
    if (!gtag) return

    gtag('event', 'contact', {
      event_category: 'phone_call',
    })
  },
  trackFacebookShare: (): void => {
    if (!gtag) return

    gtag('event', 'share', {
      event_category: 'engagement',
      event_label: 'facebook',
    })
  },
  trackGetDirections: (): void => {
    if (!gtag) return

    gtag('event', 'contact', {
      event_category: 'get_directions',
    })
  },
  trackLikeComment: (): void => {
    if (!gtag) return

    gtag('event', 'reaction', {
      event_category: 'engagement',
      event_label: 'like_comment',
    })
  },
  trackLikeMemory: (): void => {
    if (!gtag) return

    gtag('event', 'reaction', {
      event_category: 'engagement',
      event_label: 'like_memory',
    })
  },
  trackObituaryMemoryShare: (): void => {
    if (!gtag) return

    gtag('event', 'share', {
      event_category: 'engagement',
      event_label: 'obituary_memory',
    })
  },
  trackObituaryMemoryCommentShare: (): void => {
    if (!gtag) return

    gtag('event', 'share', {
      event_category: 'engagement',
      event_label: 'obituary_memory_comment',
    })
  },
  trackFlowerAdded: (product: FlowerProduct): void => {
    if (!gtag) return

    gtag('event', 'add_to_cart', {
      currency: 'USD',
      value: product.price,
      items: [FlowerProductModel.toGaItem(product)],
    })
  },
  trackFlowerRemoved: (product: FlowerProduct): void => {
    if (!gtag) return

    gtag('event', 'remove_from_cart', {
      currency: 'USD',
      value: product.price,
      items: [FlowerProductModel.toGaItem(product)],
    })
  },
  trackTreeAdded: (product: MemorialTree): void => {
    if (!gtag) return

    gtag('event', 'add_to_cart', {
      currency: 'USD',
      value: product.price_in_cents / 100.0,
      items: [MemorialTreeModel.toGaItem(product)],
    })
  },
  trackTreeRemoved: (product: MemorialTree): void => {
    if (!gtag) return

    gtag('event', 'remove_from_cart', {
      currency: 'USD',
      value: product.price_in_cents / 100.0,
      items: [MemorialTreeModel.toGaItem(product)],
    })
  },
  trackPurchase: (cart: ShoppingCart): void => {
    if (!gtag) return
    const eventData = cartEventData(cart)
    const taxInCents = cart.tax_in_cents || 0
    const deliveryChartInCents = cart.delivery_charge_in_cents || 0

    gtag('event', 'purchase', {
      ...eventData,
      transaction_id: cart.external_order_number,
      tax: taxInCents / 100.0,
      shipping: deliveryChartInCents / 100.0,
    })
  },
  trackSearchObituaries: (searchTerm: string): void => {
    if (!gtag) return

    gtag('event', 'view_search_results', {
      event_category: 'engagement',
      event_label: searchTerm,
    })
  },
  trackShippingInfoAdded: (cart: ShoppingCart): void => {
    if (!gtag) return

    gtag('event', 'add_shipping_info', cartEventData(cart))
  },
  trackPaymentInfoAdded: (cart: ShoppingCart): void => {
    if (!gtag) return

    gtag('event', 'add_payment_info', { ...cartEventData(cart), payment_type: 'Credit Card' })
  },
  trackPhoneClick: (phoneNumber: string): void => {
    if (!gtag) return

    gtag('event', 'phone_click', {
      event_category: 'veteran_services',
      event_label: phoneNumber,
    })
  },
  trackUnlikeComment: (): void => {
    if (!gtag) return

    gtag('event', 'remove_reaction', {
      event_category: 'engagement',
      event_label: 'like_comment',
    })
  },
  trackUnlikeMemory: (): void => {
    if (!gtag) return

    gtag('event', 'remove_reaction', {
      event_category: 'engagement',
      event_label: 'like_memory',
    })
  },
  trackArlingtonCemeteryInquiry: (): void => {
    if (!gtag) return

    gtag('event', 'arlington_cemetery_inquiry', {
      event_category: 'veteran_services',
    })
  },
  trackGeneralVeteranServicesInquiry: (): void => {
    if (!gtag) return

    gtag('event', 'general_veteran_services_inquiry', {
      event_category: 'veteran_services',
    })
  },
  trackArlingtonServicesButtonClick: (source: string): void => {
    if (!gtag) return

    gtag('event', 'click_on_arlington_services_button', {
      event_category: 'veteran_services',
      event_label: source,
    })
  },
  trackGeneralVeteranServicesButtonClick: (source: string): void => {
    if (!gtag) return

    gtag('event', 'click_on_general_veteran_services_button', {
      event_category: 'veteran_services',
      event_label: source,
    })
  },
  trackVeteranFormStart: (): void => {
    if (!gtag) return

    gtag('event', 'veteran_form_start', {
      event_category: 'veteran_services',
    })
  },
  trackViewFlowers: (products: FlowerProduct[], list: 'plp' | 'flower_row'): void => {
    if (!gtag) return

    gtag('event', 'view_item_list', {
      item_list_id: list,
      item_list_name: list === 'plp' ? 'Product Listing Page' : 'Flower Row',
      items: products.map(FlowerProductModel.toGaItem),
    })
  },
  trackViewFlower: (product: FlowerProduct): void => {
    if (!gtag) return

    gtag('event', 'view_item', {
      currency: 'USD',
      value: product.price,
      items: [FlowerProductModel.toGaItem(product)],
    })
  },
  trackSelectFlower: (product: FlowerProduct, list: 'plp' | 'flower_row'): void => {
    if (!gtag) return

    gtag('event', 'select_item', {
      item_list_id: list,
      item_list_name: list === 'plp' ? 'Product Listing Page' : 'Flower Row',
      items: [FlowerProductModel.toGaItem(product)],
    })
  },
  trackViewTrees: (products: MemorialTree[]): void => {
    if (!gtag) return

    gtag('event', 'view_item_list', {
      item_list_id: 'memorial_tree_products',
      item_list_name: 'Memorial Tree Products',
      items: products.map(MemorialTreeModel.toGaItem),
    })
  },
}

export default EventTrackingModel
